/**
 * F-077 — Security Audit Dashboard
 *
 * Self-assessment UI for Texas security audits. Lists audits, shows domain
 * scores, and allows control-by-control assessment.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  AUDIT_DOMAINS,
  AuditDomain,
  ControlStatus,
  SecurityAudit,
  createAudit,
  deleteAudit,
  getAudits,
  getAudit,
  getDomainScore,
  updateControlStatus,
  completeAudit,
} from './engine';

const STATUS_LABELS: Record<ControlStatus, string> = {
  IMPLEMENTED: '✅ Implemented',
  PARTIAL: '⚠️ Partial',
  NOT_IMPLEMENTED: '❌ Not Implemented',
  NOT_APPLICABLE: '➖ N/A',
};

const STATUS_OPTIONS: ControlStatus[] = ['IMPLEMENTED', 'PARTIAL', 'NOT_IMPLEMENTED', 'NOT_APPLICABLE'];

export const SecurityAuditDashboard: React.FC = () => {
  const [audits, setAudits] = useState<SecurityAudit[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<AuditDomain | null>(null);
  const [newTitle, setNewTitle] = useState('');

  const refresh = useCallback(() => {
    setAudits(getAudits());
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const selected = selectedId ? getAudit(selectedId) : undefined;

  const handleCreate = () => {
    if (!newTitle.trim()) return;
    const audit = createAudit(newTitle.trim());
    setNewTitle('');
    setSelectedId(audit.id);
    refresh();
  };

  const handleDelete = (id: string) => {
    deleteAudit(id);
    if (selectedId === id) setSelectedId(null);
    refresh();
  };

  const handleStatusChange = (controlId: string, status: ControlStatus) => {
    if (!selectedId) return;
    updateControlStatus(selectedId, controlId, status);
    refresh();
  };

  const handleComplete = () => {
    if (!selectedId) return;
    completeAudit(selectedId);
    refresh();
  };

  // ─── Audit List ────────────────────────────────────────────────────────────
  if (!selected) {
    return (
      <div data-testid="audit-dashboard" className="p-6 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">Security Audit Self-Assessment</h1>

        <div className="flex gap-2 mb-6">
          <input
            data-testid="new-audit-title"
            className="border rounded px-3 py-2 flex-1"
            placeholder="New audit title…"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          />
          <button data-testid="create-audit-btn" className="bg-blue-600 text-white px-4 py-2 rounded font-semibold" onClick={handleCreate}>
            Create Audit
          </button>
        </div>

        {audits.length === 0 && <p className="text-gray-500">No audits yet. Create one to get started.</p>}

        <div className="space-y-3">
          {audits.map((a) => (
            <div key={a.id} data-testid="audit-row" className="border rounded-lg p-4 flex justify-between items-center">
              <div className="cursor-pointer" onClick={() => setSelectedId(a.id)}>
                <h3 className="font-semibold">{a.title}</h3>
                <p className="text-sm text-gray-500">
                  Score: {a.score}% · Status: {a.status} · {new Date(a.updatedAt).toLocaleDateString()}
                </p>
              </div>
              <div className="flex gap-2">
                <button className="text-blue-600 text-sm" onClick={() => setSelectedId(a.id)}>Open</button>
                <button className="text-red-500 text-sm" onClick={() => handleDelete(a.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ─── Audit Detail ──────────────────────────────────────────────────────────
  const domainControls = selectedDomain
    ? selected.controls.filter((c) => c.domain === selectedDomain)
    : selected.controls;

  return (
    <div data-testid="audit-detail" className="p-6 max-w-5xl mx-auto">
      <div className="flex justify-between items-center mb-4">
        <div>
          <button className="text-blue-600 text-sm mb-2" onClick={() => { setSelectedId(null); setSelectedDomain(null); }}>
            ← Back to Audits
          </button>
          <h1 className="text-2xl font-bold">{selected.title}</h1>
          <p className="text-sm text-gray-500">
            Overall Score: <span data-testid="audit-score" className="font-bold text-lg">{selected.score}%</span>
            {' · '}Status: {selected.status}
          </p>
        </div>
        {selected.status !== 'COMPLETE' && (
          <button data-testid="complete-audit-btn" className="bg-green-600 text-white px-4 py-2 rounded font-semibold" onClick={handleComplete}>
            Complete Audit
          </button>
        )}
      </div>

      {/* Domain Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          className={`px-3 py-1 rounded text-sm ${!selectedDomain ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
          onClick={() => setSelectedDomain(null)}
        >
          All ({selected.controls.length})
        </button>
        {AUDIT_DOMAINS.map((d) => (
          <button
            key={d.key}
            data-testid="domain-tab"
            className={`px-3 py-1 rounded text-sm ${selectedDomain === d.key ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
            onClick={() => setSelectedDomain(d.key)}
          >
            {d.label} ({getDomainScore(selected, d.key)}%)
          </button>
        ))}
      </div>

      {/* Controls Table */}
      <div className="space-y-3">
        {domainControls.map((control) => (
          <div key={control.id} data-testid="control-row" className="border rounded-lg p-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h4 className="font-semibold">{control.id}: {control.title}</h4>
                <p className="text-sm text-gray-600 mt-1">{control.description}</p>
                {control.statutoryRef && (
                  <p className="text-xs text-blue-600 mt-1">📜 {control.statutoryRef}</p>
                )}
              </div>
              <select
                data-testid="control-status-select"
                className="border rounded px-2 py-1 text-sm ml-4"
                value={control.status}
                onChange={(e) => handleStatusChange(control.id, e.target.value as ControlStatus)}
                disabled={selected.status === 'COMPLETE'}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SecurityAuditDashboard;
